import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowLeft, ArrowRight, Ban, BookOpen, Check, CreditCard, Flag, GraduationCap,
  LifeBuoy, MessageCircle, Send, UsersRound, X,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { MemberProfilePhoto } from "@/components/PersonPortraitAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

type DestinationKey = "resources" | "messages" | "groups" | "micro-learning" | "support" | "billing";

const destinations: Record<DestinationKey, {
  eyebrow: string;
  title: string;
  description: string;
  Icon: typeof BookOpen;
  accent: string;
}> = {
  resources: {
    eyebrow: "Member resources",
    title: "Resources for meaningful work",
    description: "A focused home for guides, conversations, and practical ideas that help CHI Alliance members keep moving forward.",
    Icon: BookOpen,
    accent: "#7DD3FC",
  },
  messages: {
    eyebrow: "Member space",
    title: "Messages",
    description: "Start a private conversation with a member who has chosen to be reachable.",
    /* A conversation, not a mailbox: this page threads replies rather than sending mail. */
    Icon: MessageCircle,
    accent: "#F9A8D4",
  },
  groups: {
    eyebrow: "Member space",
    title: "Groups",
    description: "Find smaller circles for shared interests, practical questions, and honest exchange.",
    Icon: UsersRound,
    accent: "#86EFAC",
  },
  "micro-learning": {
    eyebrow: "Micro-learning",
    title: "Small lessons, lasting momentum",
    description: "Short, practical learning moments for people building more human organizations. New lessons will appear here as they are released.",
    Icon: GraduationCap,
    accent: "#FCD34D",
  },
  support: {
    eyebrow: "Account support",
    title: "How can we help?",
    description: "For account, membership, event, or directory questions, reach out to the CHI Alliance team and we’ll help you find the next step.",
    Icon: LifeBuoy,
    accent: "#C4B5FD",
  },
  billing: {
    eyebrow: "Account",
    title: "Membership and billing",
    description: "Review membership benefits and choose the access level that fits your current season.",
    Icon: CreditCard,
    accent: "#F9A8D4",
  },
};

const surface = {
  background: "linear-gradient(145deg, rgba(22,30,54,0.9), rgba(8,13,29,0.86) 58%, rgba(29,12,43,0.78))",
  border: "1px solid rgba(191,201,255,0.13)",
  borderRadius: "1.1rem",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 18px 42px rgba(0,0,0,0.2)",
};
const button = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: ".45rem",
  minHeight: 42, padding: ".65rem .9rem", borderRadius: ".6rem",
  border: "1px solid rgba(255,255,255,.15)", color: "#F8FAFC",
  background: "rgba(255,255,255,.06)", fontFamily: "'Inter', sans-serif",
  fontSize: ".78rem", fontWeight: 750, cursor: "pointer",
};

function StateCard({ children, tone = "normal" }: { children: React.ReactNode; tone?: "normal" | "error" }) {
  return (
    <div role={tone === "error" ? "alert" : undefined} style={{ ...surface, padding: "1.5rem", color: tone === "error" ? "#FCA5A5" : "#AAB7CB", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
      {children}
    </div>
  );
}

function MemberAvatar({
  name,
  photoUrl,
  memberPhotoUrl,
}: {
  name: string;
  photoUrl?: string | null;
  memberPhotoUrl?: string | null;
}) {
  return (
    <MemberProfilePhoto
      name={name}
      speakerPortraitUrl={photoUrl}
      memberPhotoUrl={memberPhotoUrl}
      size={42}
      accent="#F9A8D4"
    />
  );
}

function MessagesSpace() {
  const { isLoggedIn } = useAuth();
  const [location, navigate] = useLocation();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [recipientId, setRecipientId] = useState("");
  const [body, setBody] = useState("");
  const [notice, setNotice] = useState<{ text: string; tone: "normal" | "error" } | null>(null);
  const [reportTarget, setReportTarget] = useState<{ messageId?: number; label: string } | null>(null);
  const [reportReason, setReportReason] = useState<"spam" | "harassment" | "inappropriate" | "other">("inappropriate");
  const [reportDetails, setReportDetails] = useState("");
  const utils = trpc.useUtils();
  const conversations = trpc.members.conversations.useQuery(undefined, { enabled: isLoggedIn, retry: false });
  const messageable = trpc.members.messageableMembers.useQuery(undefined, { enabled: isLoggedIn, retry: false });
  const conversation = trpc.members.conversation.useQuery(
    { conversationId: selectedId ?? 0 },
    { enabled: isLoggedIn && selectedId !== null, retry: false },
  );
  const startConversation = trpc.members.startConversation.useMutation();
  const sendMessage = trpc.members.sendMessage.useMutation();
  const blockMember = trpc.members.blockMember.useMutation();
  const unblockMember = trpc.members.unblockMember.useMutation();
  const reportMessage = trpc.members.reportMessage.useMutation();
  const targetFromUrl = useMemo(() => new URLSearchParams(location.split("?")[1] ?? "").get("member") ?? "", [location]);

  useEffect(() => {
    if (selectedId === null && conversations.data?.length) setSelectedId(conversations.data[0].id);
  }, [conversations.data, selectedId]);

  useEffect(() => {
    if (!targetFromUrl || !messageable.data || startConversation.isPending) return;
    if (!messageable.data.some(member => member.id === targetFromUrl)) return;
    setRecipientId(targetFromUrl);
    void startConversation.mutateAsync({ memberId: targetFromUrl }).then(result => {
      setSelectedId(result.id);
      void utils.members.conversations.invalidate();
      navigate("/messages");
    }).catch(error => setNotice({
      text: error instanceof Error ? error.message : "This conversation could not be started.",
      tone: "error",
    }));
    // The query string is cleared after the attempt, so this runs once per directory action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetFromUrl, messageable.data]);

  if (!isLoggedIn) {
    return <StateCard><strong style={{ color: "#F8FAFC" }}>Sign in to use member messages.</strong><br />Private conversations are available to authenticated community members. <Link href="/login" style={{ color: "#F9A8D4" }}>Sign in</Link></StateCard>;
  }
  if (conversations.isLoading || messageable.isLoading) return <StateCard>Loading your member space…</StateCard>;
  if (conversations.isError || messageable.isError) return <StateCard tone="error">Messages could not be loaded. Refresh the page to try again.</StateCard>;
  const conversationList = conversations.data ?? [];
  const messageableList = messageable.data ?? [];

  const start = async () => {
    if (!recipientId) return;
    setNotice(null);
    try {
      const result = await startConversation.mutateAsync({ memberId: recipientId });
      setSelectedId(result.id);
      await utils.members.conversations.invalidate();
    } catch (error) {
      setNotice({
        text: error instanceof Error ? error.message : "This conversation could not be started.",
        tone: "error",
      });
    }
  };
  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedId || !body.trim()) return;
    setNotice(null);
    try {
      await sendMessage.mutateAsync({ conversationId: selectedId, body: body.trim() });
      setBody("");
      await Promise.all([conversation.refetch(), utils.members.conversations.invalidate()]);
    } catch (error) {
      setNotice({
        text: error instanceof Error ? error.message : "Your message could not be sent.",
        tone: "error",
      });
    }
  };
  const toggleBlock = async () => {
    if (!selectedId || !conversation.data) return;
    const isBlocked = conversation.data.blockedByViewer;
    const confirmed = window.confirm(
      isBlocked
        ? `Unblock ${conversation.data.member.name}? They will be able to message you again.`
        : `Block ${conversation.data.member.name}? Neither of you will be able to send new private messages.`,
    );
    if (!confirmed) return;
    setNotice(null);
    try {
      if (isBlocked) await unblockMember.mutateAsync({ conversationId: selectedId });
      else await blockMember.mutateAsync({ conversationId: selectedId });
      await Promise.all([
        conversation.refetch(),
        utils.members.conversations.invalidate(),
        utils.members.messageableMembers.invalidate(),
      ]);
      setNotice({
        text: isBlocked ? "This member has been unblocked." : "This member is now blocked.",
        tone: "normal",
      });
    } catch (error) {
      setNotice({
        text: error instanceof Error ? error.message : "The block setting could not be changed.",
        tone: "error",
      });
    }
  };
  const submitReport = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedId || !reportTarget) return;
    setNotice(null);
    try {
      const result = await reportMessage.mutateAsync({
        conversationId: selectedId,
        messageId: reportTarget.messageId,
        reason: reportReason,
        details: reportDetails.trim() || undefined,
      });
      setReportTarget(null);
      setReportDetails("");
      setReportReason("inappropriate");
      setNotice({
        text: result.alreadyReported
          ? "You already reported this content. The report is still available to administrators."
          : "Report sent to the CHI Alliance administrators for review.",
        tone: "normal",
      });
    } catch (error) {
      setNotice({
        text: error instanceof Error ? error.message : "Your report could not be sent.",
        tone: "error",
      });
    }
  };

  return (
    <div style={{ width: "min(100%, 980px)", display: "grid", gap: "1rem" }}>
      <div style={{ ...surface, padding: "1rem", display: "flex", flexWrap: "wrap", alignItems: "center", gap: ".7rem" }}>
        <MessageCircle size={18} color="#F9A8D4" aria-hidden="true" />
        <label htmlFor="message-recipient" style={{ color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".75rem", fontWeight: 700 }}>New conversation</label>
        <select id="message-recipient" value={recipientId} onChange={event => setRecipientId(event.target.value)} style={{ flex: "1 1 220px", minHeight: 42, padding: ".6rem .7rem", borderRadius: ".6rem", border: "1px solid rgba(255,255,255,.14)", color: "#F8FAFC", background: "#11182B", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }}>
          <option value="">Choose a member who accepts messages</option>
          {messageableList.map(member => <option key={member.id} value={member.id}>{member.name}{member.title ? ` · ${member.title}` : ""}</option>)}
        </select>
        <button type="button" onClick={() => void start()} disabled={!recipientId || startConversation.isPending} style={{ ...button, color: "#FFF", border: 0, background: "linear-gradient(110deg,#A73889,#E91E8C)", opacity: !recipientId || startConversation.isPending ? .6 : 1 }}>
          {startConversation.isPending ? "Opening…" : "Open chat"} <ArrowRight size={14} />
        </button>
      </div>

      {notice && <StateCard tone={notice.tone}>{notice.text}</StateCard>}
      {messageableList.length === 0 && conversationList.length === 0 && <StateCard><strong style={{ color: "#F8FAFC" }}>No messageable members yet.</strong><br />Members appear here after they publish their directory profile and turn on private messages. You can update that setting from <Link href="/my-profile" style={{ color: "#F9A8D4" }}>your profile</Link>.</StateCard>}

      {(conversationList.length > 0 || messageableList.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(190px, .7fr) minmax(0, 1.5fr)", minHeight: 520, ...surface, overflow: "hidden" }}>
          <aside style={{ borderRight: "1px solid rgba(255,255,255,.09)", background: "rgba(5,9,22,.3)" }} aria-label="Conversations">
            <div style={{ padding: "1rem", borderBottom: "1px solid rgba(255,255,255,.08)", color: "#7D8AA8", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".66rem", fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" }}>Your conversations</div>
            {conversationList.length === 0 ? <p style={{ padding: "1rem", color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>Choose a member above to begin.</p> : conversationList.map(item => (
              <button key={item.id} type="button" onClick={() => { setSelectedId(item.id); setReportTarget(null); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: ".65rem", padding: ".8rem 1rem", textAlign: "left", border: 0, borderBottom: "1px solid rgba(255,255,255,.06)", color: "#F8FAFC", background: selectedId === item.id ? "rgba(233,30,140,.12)" : "transparent", cursor: "pointer" }}>
                <MemberAvatar name={item.member.name} photoUrl={item.member.photoUrl} memberPhotoUrl={item.member.memberPhotoUrl} />
                <span style={{ minWidth: 0, flex: 1 }}><strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".78rem" }}>{item.member.name}</strong><small style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: ".2rem", color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: ".68rem" }}>{item.lastMessage?.body ?? "New conversation"}</small></span>
                {item.unreadCount > 0 && <span aria-label={`${item.unreadCount} unread`} style={{ minWidth: 20, padding: ".2rem .35rem", borderRadius: 99, color: "#FFF", background: "#E91E8C", fontSize: ".62rem", textAlign: "center" }}>{item.unreadCount}</span>}
              </button>
            ))}
          </aside>
          <section style={{ display: "flex", flexDirection: "column", minWidth: 0 }} aria-label="Conversation">
            {!selectedId ? <div style={{ display: "grid", flex: 1, placeItems: "center", padding: "2rem", color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".86rem", textAlign: "center" }}>Select a conversation or start a new one above.</div> : conversation.isLoading ? <div style={{ display: "grid", flex: 1, placeItems: "center", color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".86rem" }}>Loading conversation…</div> : conversation.isError || !conversation.data ? <StateCard tone="error">This conversation could not be loaded. Choose another conversation and try again.</StateCard> : (
              <>
                <header style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: ".7rem", padding: "1rem 1.2rem", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
                  <MemberAvatar name={conversation.data.member.name} photoUrl={conversation.data.member.photoUrl} memberPhotoUrl={conversation.data.member.memberPhotoUrl} />
                  <div style={{ flex: "1 1 150px" }}>
                    <strong style={{ display: "block", color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".9rem" }}>{conversation.data.member.name}</strong>
                    <span style={{ color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>{conversation.data.member.title || conversation.data.member.company || "CHI Alliance member"}</span>
                  </div>
                  <button type="button" onClick={() => setReportTarget({ label: "this conversation" })} style={{ ...button, minHeight: 34, padding: ".45rem .6rem", color: "#FBBF24", background: "transparent" }}>
                    <Flag size={13} /> Report
                  </button>
                  <button type="button" onClick={() => void toggleBlock()} disabled={blockMember.isPending || unblockMember.isPending} style={{ ...button, minHeight: 34, padding: ".45rem .6rem", color: conversation.data.blockedByViewer ? "#86EFAC" : "#FCA5A5", background: "transparent" }}>
                    <Ban size={13} /> {conversation.data.blockedByViewer ? "Unblock" : "Block"}
                  </button>
                </header>
                {reportTarget && (
                  <form onSubmit={submitReport} style={{ display: "grid", gap: ".65rem", padding: "1rem 1.2rem", borderBottom: "1px solid rgba(251,191,36,.2)", background: "rgba(251,191,36,.06)" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
                      <strong style={{ color: "#F8FAFC", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".8rem" }}>Report {reportTarget.label}</strong>
                      <button type="button" aria-label="Cancel report" onClick={() => setReportTarget(null)} style={{ border: 0, color: "#94A3B8", background: "transparent", cursor: "pointer" }}><X size={16} /></button>
                    </div>
                    <label style={{ display: "grid", gap: ".3rem", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>
                      Reason
                      <select value={reportReason} onChange={event => setReportReason(event.target.value as typeof reportReason)} style={{ minHeight: 38, padding: ".5rem .6rem", borderRadius: ".5rem", border: "1px solid rgba(255,255,255,.14)", color: "#F8FAFC", background: "#11182B" }}>
                        <option value="inappropriate">Inappropriate content</option>
                        <option value="harassment">Harassment or unwanted contact</option>
                        <option value="spam">Spam</option>
                        <option value="other">Something else</option>
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: ".3rem", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: ".72rem" }}>
                      Additional details (optional)
                      <textarea value={reportDetails} onChange={event => setReportDetails(event.target.value)} maxLength={2000} rows={2} placeholder="Tell administrators what happened…" style={{ resize: "vertical", minHeight: 48, padding: ".6rem", borderRadius: ".5rem", border: "1px solid rgba(255,255,255,.14)", color: "#F8FAFC", background: "#11182B" }} />
                    </label>
                    <button type="submit" disabled={reportMessage.isPending} style={{ ...button, justifySelf: "start", color: "#111827", border: 0, background: "#FBBF24", opacity: reportMessage.isPending ? .6 : 1 }}>
                      <Flag size={14} /> {reportMessage.isPending ? "Sending…" : "Send report"}
                    </button>
                  </form>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: ".7rem", flex: 1, maxHeight: 380, overflowY: "auto", padding: "1rem 1.2rem" }}>
                  {conversation.data.messages.length === 0 ? <p style={{ margin: "auto", color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".82rem", textAlign: "center" }}>Say hello and start the conversation.</p> : conversation.data.messages.map(message => (
                    <div key={message.id} style={{ display: "grid", gap: ".25rem", alignSelf: message.isMine ? "flex-end" : "flex-start", justifyItems: message.isMine ? "end" : "start", maxWidth: "min(80%, 520px)" }}>
                      <div style={{ padding: ".7rem .85rem", borderRadius: ".8rem", color: "#E7ECF5", background: message.isMine ? "linear-gradient(110deg,rgba(167,56,137,.9),rgba(233,30,140,.72))" : "rgba(255,255,255,.07)", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>
                        {message.body}
                        <small style={{ display: "block", marginTop: ".3rem", color: "rgba(255,255,255,.58)", fontSize: ".62rem" }}>{new Date(message.createdAt).toLocaleString()}</small>
                      </div>
                      {!message.isMine && <button type="button" onClick={() => setReportTarget({ messageId: message.id, label: "this message" })} style={{ border: 0, padding: ".15rem .25rem", color: "#94A3B8", background: "transparent", fontFamily: "'Inter', sans-serif", fontSize: ".62rem", cursor: "pointer" }}>Report message</button>}
                    </div>
                  ))}
                </div>
                {conversation.data.canSend ? (
                  <form onSubmit={send} style={{ display: "flex", gap: ".6rem", padding: "1rem 1.2rem", borderTop: "1px solid rgba(255,255,255,.08)" }}><label htmlFor="message-body" className="sr-only">Write a message</label><textarea id="message-body" value={body} onChange={event => setBody(event.target.value)} maxLength={4000} rows={2} placeholder="Write a private message…" style={{ flex: 1, resize: "vertical", minHeight: 44, padding: ".7rem .75rem", borderRadius: ".6rem", border: "1px solid rgba(255,255,255,.14)", color: "#F8FAFC", background: "#11182B", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }} /><button type="submit" aria-label="Send message" disabled={!body.trim() || sendMessage.isPending} style={{ ...button, alignSelf: "flex-end", color: "#FFF", border: 0, background: "linear-gradient(110deg,#A73889,#E91E8C)", opacity: !body.trim() || sendMessage.isPending ? .6 : 1 }}><Send size={16} /></button></form>
                ) : (
                  <p role="status" style={{ margin: 0, padding: "1rem 1.2rem", borderTop: "1px solid rgba(255,255,255,.08)", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: ".75rem" }}>
                    {conversation.data.blockedByViewer ? "You blocked this member. Unblock them to resume messaging." : conversation.data.blockedByMember ? "Private messages are unavailable in this conversation." : "This member is not accepting private messages."}
                  </p>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function GroupsSpace() {
  const { isLoggedIn } = useAuth();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [postBody, setPostBody] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const utils = trpc.useUtils();
  const groups = trpc.members.groups.useQuery(undefined, { enabled: isLoggedIn, retry: false });
  const group = trpc.members.group.useQuery({ groupId: selectedId ?? 0 }, { enabled: isLoggedIn && selectedId !== null, retry: false });
  const join = trpc.members.joinGroup.useMutation();
  const leave = trpc.members.leaveGroup.useMutation();
  const createPost = trpc.members.createGroupPost.useMutation();

  useEffect(() => {
    if (selectedId === null && groups.data?.length) setSelectedId(groups.data[0].id);
  }, [groups.data, selectedId]);

  if (!isLoggedIn) return <StateCard><strong style={{ color: "#F8FAFC" }}>Sign in to join member groups.</strong><br />Group discussions are available to authenticated community members. <Link href="/login" style={{ color: "#F9A8D4" }}>Sign in</Link></StateCard>;
  if (groups.isLoading) return <StateCard>Loading member groups…</StateCard>;
  if (groups.isError) return <StateCard tone="error">Groups could not be loaded. Refresh the page to try again.</StateCard>;
  const groupList = groups.data ?? [];
  if (groupList.length === 0) return <StateCard><strong style={{ color: "#F8FAFC" }}>No groups are available yet.</strong><br />New member circles will appear here when they are ready.</StateCard>;

  const selected = groupList.find(item => item.id === selectedId);
  const updateMembership = async () => {
    if (!selectedId) return;
    setNotice(null);
    try {
      if (selected?.joined) await leave.mutateAsync({ groupId: selectedId });
      else await join.mutateAsync({ groupId: selectedId });
      await Promise.all([utils.members.groups.invalidate(), group.refetch()]);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Your group membership could not be changed.");
    }
  };
  const publish = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedId || !postBody.trim()) return;
    setNotice(null);
    try {
      await createPost.mutateAsync({ groupId: selectedId, body: postBody.trim() });
      setPostBody("");
      await Promise.all([group.refetch(), utils.members.groups.invalidate()]);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Your post could not be published.");
    }
  };

  return (
    <div style={{ width: "min(100%, 980px)", display: "grid", gap: "1rem" }}>
      {notice && <StateCard tone="error">{notice}</StateCard>}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(205px, .72fr) minmax(0, 1.5fr)", minHeight: 520, ...surface, overflow: "hidden" }}>
        <aside style={{ borderRight: "1px solid rgba(255,255,255,.09)", background: "rgba(5,9,22,.3)" }} aria-label="Available groups">
          <div style={{ padding: "1rem", borderBottom: "1px solid rgba(255,255,255,.08)", color: "#7D8AA8", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".66rem", fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" }}>Explore groups</div>
          {groupList.map(item => <button key={item.id} type="button" onClick={() => setSelectedId(item.id)} style={{ width: "100%", padding: "1rem", textAlign: "left", border: 0, borderBottom: "1px solid rgba(255,255,255,.06)", color: "#F8FAFC", background: selectedId === item.id ? "rgba(134,239,172,.1)" : "transparent", cursor: "pointer" }}><strong style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".8rem" }}>{item.name}</strong><span style={{ display: "block", marginTop: ".25rem", color: "#86EFAC", fontFamily: "'Inter', sans-serif", fontSize: ".66rem" }}>{item.memberCount} member{item.memberCount === 1 ? "" : "s"}{item.joined ? " · Joined" : ""}</span></button>)}
        </aside>
        <section style={{ minWidth: 0 }} aria-label="Group discussion">
          {group.isLoading ? <div style={{ display: "grid", height: "100%", placeItems: "center", color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".86rem" }}>Loading group…</div> : group.isError || !group.data ? <StateCard tone="error">This group could not be loaded. Choose another group and try again.</StateCard> : (
            <>
              <header style={{ padding: "1.2rem", borderBottom: "1px solid rgba(255,255,255,.08)" }}><div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem" }}><div><span style={{ color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".65rem", fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" }}>{group.data.topic}</span><h2 style={{ margin: ".35rem 0 0", color: "#FFF", fontFamily: "'DM Serif Display', Georgia, serif", fontSize: "1.8rem", fontWeight: 400 }}>{group.data.name}</h2></div><button type="button" onClick={() => void updateMembership()} disabled={join.isPending || leave.isPending} style={{ ...button, color: group.data.joined ? "#CBD5E1" : "#FFF", borderColor: group.data.joined ? "rgba(255,255,255,.15)" : "rgba(134,239,172,.4)", background: group.data.joined ? "transparent" : "rgba(134,239,172,.12)" }}>{group.data.joined ? <><Check size={14} /> Joined</> : <><UsersRound size={14} /> Join group</>}</button></div><p style={{ maxWidth: "65ch", margin: ".65rem 0 0", color: "#AAB7CB", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>{group.data.description}</p></header>
              <div style={{ display: "flex", flexDirection: "column", gap: ".75rem", minHeight: 270, maxHeight: 330, overflowY: "auto", padding: "1.1rem 1.2rem" }}>{group.data.posts.length === 0 ? <p style={{ margin: "auto", color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".82rem", textAlign: "center" }}>This group is ready for its first conversation.</p> : group.data.posts.map(post => <article key={post.id} style={{ padding: ".75rem .85rem", border: "1px solid rgba(255,255,255,.08)", borderRadius: ".7rem", background: "rgba(255,255,255,.035)" }}><div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}><strong style={{ color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".76rem" }}>{post.author}</strong><time style={{ color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: ".64rem" }}>{new Date(post.createdAt).toLocaleDateString()}</time></div><p style={{ margin: ".4rem 0 0", color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{post.body}</p></article>)}</div>
              {group.data.joined ? <form onSubmit={publish} style={{ display: "flex", gap: ".6rem", padding: "1rem 1.2rem", borderTop: "1px solid rgba(255,255,255,.08)" }}><label htmlFor="group-post-body" className="sr-only">Write a group post</label><textarea id="group-post-body" value={postBody} onChange={event => setPostBody(event.target.value)} maxLength={4000} rows={2} placeholder="Share something with the group…" style={{ flex: 1, resize: "vertical", minHeight: 44, padding: ".7rem .75rem", borderRadius: ".6rem", border: "1px solid rgba(255,255,255,.14)", color: "#F8FAFC", background: "#11182B", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }} /><button type="submit" aria-label="Publish group post" disabled={!postBody.trim() || createPost.isPending} style={{ ...button, alignSelf: "flex-end", color: "#FFF", border: 0, background: "linear-gradient(110deg,#3A9B70,#67C58E)", opacity: !postBody.trim() || createPost.isPending ? .6 : 1 }}><Send size={16} /></button></form> : <p style={{ margin: 0, padding: "1rem 1.2rem", borderTop: "1px solid rgba(255,255,255,.08)", color: "#8491A7", fontFamily: "'Inter', sans-serif", fontSize: ".75rem" }}>Join this group to participate in the conversation.</p>}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default function MemberDestination({ destination }: { destination: DestinationKey }) {
  const [, navigate] = useLocation();
  const page = destinations[destination];
  const Icon = page.Icon;
  const interactive = destination === "messages" || destination === "groups";

  return (
    <div className="member-destination-page">
      <Navigation />
      <main className={`member-destination${interactive ? " member-destination--interactive" : ""}`}>
        <div className="member-destination__glow" style={{ background: `radial-gradient(circle, ${page.accent}28, transparent 68%)` }} aria-hidden="true" />
        <section className={`member-destination__card${interactive ? " member-destination__card--interactive" : ""}`} aria-labelledby="member-destination-title">
          <span className="member-destination__icon" style={{ color: page.accent, borderColor: `${page.accent}55`, background: `${page.accent}14` }}><Icon size={27} aria-hidden="true" /></span>
          <p className="member-destination__eyebrow">{page.eyebrow}</p>
          <h1 id="member-destination-title">{page.title}</h1>
          <p className="member-destination__description">{page.description}</p>
          {destination === "messages" ? <MessagesSpace /> : destination === "groups" ? <GroupsSpace /> : (
            <div className="member-destination__actions">
              {destination === "billing" ? <Link href="/membership" className="member-destination__primary">View membership details <ArrowRight size={16} /></Link> : destination === "support" ? <a className="member-destination__primary" href="mailto:info@thechialliance.com">Email the CHI Alliance <ArrowRight size={16} /></a> : <Link href="/community" className="member-destination__primary">Return to Community <ArrowRight size={16} /></Link>}
              <button type="button" className="member-destination__secondary" onClick={() => navigate("/my-profile")}>Open my profile</button>
            </div>
          )}
          {interactive && <Link href="/community" style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", marginTop: "1.25rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: ".75rem", textDecoration: "none" }}><ArrowLeft size={14} /> Back to Community</Link>}
        </section>
      </main>
      <Footer />
    </div>
  );
}